import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import { get } from 'lodash';
import globalLogger from '../logger/logger';

const logger = globalLogger.createChannel('auth');

// Utils
import { getCurrentUserToken, getHasRememberedBetterAuthSessionUser } from './authService';
import { navigateToLogin, navigateToRegister, isSafeRedirectUrl, safeRedirectTo } from '../reducers/navigationActions';
import { fetchCurrentUser } from '../user/currentUserActions';

// Selectors
import { getCurrentUser } from '../user/currentUserSelector';
import { getLocation } from '../app/routingSelector';

import PageLoader from '../components/loaders/PageLoader';

const currentUserAvailableSelector = createSelector(
    getCurrentUser,
    (currentUser) => currentUser.get('authenticated') && currentUser.get('fetched'),
);

/**
 * Selector used to build up the user data for Intercom
 */

const authenticatedRouteSelector = createStructuredSelector({
    isUserAvailable: currentUserAvailableSelector,
    location: getLocation,
});

const mapDispatchToProps = (dispatch) => ({
    dispatchNavigateToLogin: (params) => dispatch(navigateToLogin(params)),
    dispatchNavigateToRegister: (params) => dispatch(navigateToRegister(params)),
    dispatchFetchCurrentUser: () => dispatch(fetchCurrentUser()),
});

const getHasLocalAuthHint = () => !!getCurrentUserToken() || getHasRememberedBetterAuthSessionUser();

export const AuthenticatedRoute = (props) => {
    const {
        isUserAvailable,
        children,
        location,
        redirectParams,
        redirectToRegister,
        redirectToUrl,
        dispatchNavigateToLogin,
        dispatchNavigateToRegister,
        dispatchFetchCurrentUser,
    } = props;
    const [sessionCheckComplete, setSessionCheckComplete] = useState(false);

    useEffect(() => {
        let isMounted = true;

        if (isUserAvailable || !getHasLocalAuthHint()) {
            setSessionCheckComplete(true);
            return;
        }

        dispatchFetchCurrentUser()
            .catch(() => {
                logger.warn('Failed to fetch current user while guarding authenticated route');
            })
            .finally(() => {
                if (isMounted) setSessionCheckComplete(true);
            });

        return () => {
            isMounted = false;
        };
    }, [dispatchFetchCurrentUser, isUserAvailable, location?.pathname]);

    // redirect user if session restoration failed
    useEffect(() => {
        const pathname = get(location, 'pathname');

        if (!sessionCheckComplete || isUserAvailable || getHasLocalAuthHint()) return;

        let navigateFn;

        if (redirectToUrl && isSafeRedirectUrl(redirectToUrl)) {
            navigateFn = () => {
                safeRedirectTo(redirectToUrl);
            };
        } else if (redirectToRegister) {
            navigateFn = dispatchNavigateToRegister;
        } else {
            navigateFn = dispatchNavigateToLogin;
        }

        const params = redirectParams || { from: encodeURIComponent(pathname) };

        navigateFn(params);
    }, [
        sessionCheckComplete,
        isUserAvailable,
        location?.pathname,
        redirectParams,
        redirectToRegister,
        redirectToUrl,
        dispatchNavigateToLogin,
        dispatchNavigateToRegister,
    ]);

    if (!isUserAvailable) return <PageLoader />;

    return children;
};

AuthenticatedRoute.propTypes = {
    redirectToRegister: PropTypes.bool,
    isUserAvailable: PropTypes.bool,
    redirectParams: PropTypes.object,
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    location: PropTypes.object,
    requiredUserRoles: PropTypes.array,
    currentUserRole: PropTypes.string,
    redirectToUrl: PropTypes.string,

    dispatchNavigateToLogin: PropTypes.func,
    dispatchNavigateToRegister: PropTypes.func,
    dispatchFetchCurrentUser: PropTypes.func,
};

export default connect(authenticatedRouteSelector, mapDispatchToProps)(AuthenticatedRoute);
